import { Message } from 'lib-shared-legacy-types/src/message';

export type PredictedConversationQualityScoreForMessages = {
  [messageId: string]: PredictedConversationQualityScore;
};
export type PredictedConversationQualityScore = {
  /** Percentage, whole number */
  qualityScore: number;
  qualityScoreTrend: 'decreasing' | 'neutral' | 'increasing';
  variant: 'positive' | 'negative';
};
/**
 * Derives predicted quality scores and trends for a series of messages.
 *
 * This function processes an array of messages, calculating a predicted CSAT
 * (Customer Satisfaction) value and a quality score trend for each message. The
 * trend is determined by comparing the current message's score with the
 * previous message's score.
 *
 * - The function rounds all conversation value metric scores to two decimal
 *   places.
 * - The first message in the array will always have a 'neutral' trend.
 * - Messages with null or undefined `conversation_value_metric_score` are
 *   skipped.
 */
export function derivePredictedQualityScoresForMessages(
  messages: Message[],
): PredictedConversationQualityScoreForMessages {
  const result = messages.reduce<{
    predictedScoresWithTrends: PredictedConversationQualityScoreForMessages;
    previousQualityScore: number | null;
  }>(
    (accumulator, message) => {
      const {
        conversation_value_metric_score: conversationValueMetricScore,
        id: messageId,
      } = message;

      if (
        messageId && // can be null
        conversationValueMetricScore !== null &&
        conversationValueMetricScore !== undefined
      ) {
        const roundedQualityScore = Math.round(
          Number(conversationValueMetricScore) * 100,
        );
        const previousQualityScore = accumulator.previousQualityScore;

        const qualityScoreTrend =
          previousQualityScore === null
            ? 'neutral'
            : roundedQualityScore > previousQualityScore
            ? 'increasing'
            : roundedQualityScore < previousQualityScore
            ? 'decreasing'
            : 'neutral';

        accumulator.predictedScoresWithTrends[messageId] = {
          qualityScore: roundedQualityScore,
          qualityScoreTrend,
          variant: roundedQualityScore >= 50 ? 'positive' : 'negative',
        };

        accumulator.previousQualityScore = roundedQualityScore;
      }

      return accumulator;
    },
    { predictedScoresWithTrends: {}, previousQualityScore: null },
  );

  return result.predictedScoresWithTrends;
}
